import React, { FunctionComponent, useState, useCallback, useContext, useEffect } from 'react';
import { useEventListener } from '@24i/player-utils-react';
import { PlayerUIContext, dragSeekingEnded, dragSeekingStarted } from 'LocalGluons/components/Player/Context';
import { SeekingContainer } from '../../styled';

const PERCENT = 100;

interface ISeekingManager {
    parentElement: HTMLDivElement;
    onSeekingProgress: { (progress: number): void };
    onSeekingStart: { (): void };
    onSeekingEnd: { (): void };
    onHoverStart: { (): void };
    onHoverEnd: { (): void };
    isSeekingDisabled: boolean;
}
export const SeekingManager: FunctionComponent<ISeekingManager> = ({
    parentElement,
    onSeekingProgress,
    onSeekingStart,
    onSeekingEnd,
    onHoverStart,
    onHoverEnd,
    isSeekingDisabled,
}) => {
    const [isGestureInProgress, setIsGestureInProgress] = useState(false);
    const [isHovering, setIsHovering] = useState(false);
    const { dispatch } = useContext(PlayerUIContext);

    /**
     * Calculates and report progress.
     */
    const getAndReportProgress = useCallback(
        ({ pageX }) => {
            if (!parentElement || pageX === undefined) return;
            const { left, width } = parentElement.getBoundingClientRect();
            const progress = ((pageX - left) / width) * PERCENT;
            const progressInPercent = Math.max(0, Math.min(PERCENT, progress));
            onSeekingProgress(progressInPercent);
        },
        [parentElement, onSeekingProgress]
    );

    /**
     * Stop event propagation.
     */
    const preventEvent = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
    }, []);

    /**
     * Start the gesture. Report gesture start, and dispatch it to UI context.
     */
    const onGestureStarted = useCallback(() => {
        setIsGestureInProgress(true);
        onSeekingStart();
        dispatch(dragSeekingStarted());
    }, [setIsGestureInProgress, onSeekingStart]);

    /**
     * End the gesture. Report gesture end, and dispatch it to UI context.
     */
    const onGestureEnded = useCallback(() => {
        setIsGestureInProgress(false);
        onSeekingEnd();
        dispatch(dragSeekingEnded());
    }, [onSeekingEnd, setIsGestureInProgress]);

    /**
     * Start the gesture.
     */
    const onMouseDown = useCallback(
        (event) => {
            if (isSeekingDisabled) return;
            preventEvent(event);
            onGestureStarted();
            getAndReportProgress(event);
        },
        [preventEvent, onGestureStarted, getAndReportProgress, isSeekingDisabled]
    );

    /**
     * Gesture in progress, report it's progress.
     */
    const onMouseMove = useCallback(
        (event) => {
            if (isGestureInProgress || isHovering) {
                preventEvent(event);
                getAndReportProgress(event);
            }
        },
        [isGestureInProgress, isHovering, preventEvent, getAndReportProgress]
    );

    /**
     * If gesture is in progress, stop the event.
     */
    const onMouseUp = useCallback(
        (event) => {
            if (isGestureInProgress) preventEvent(event);
        },
        [isGestureInProgress, preventEvent]
    );

    /**
     * On click comes after onMouseUp, so we finish the gesture there.
     */
    const onClick = useCallback(
        (event) => {
            if (isGestureInProgress) {
                preventEvent(event);
                onGestureEnded();
            }
        },
        [isGestureInProgress, preventEvent, onGestureEnded]
    );

    /**
     * Start the hover.
     */
    const onMouseEnter = useCallback(
        (event) => {
            if (isSeekingDisabled) return;
            setIsHovering(true);
            onHoverStart();
            getAndReportProgress(event);
        },
        [onHoverStart, getAndReportProgress, isSeekingDisabled]
    );

    /**
     * End the hover.
     */
    const onMouseLeave = useCallback(() => {
        setIsHovering(false);
        onHoverEnd();
    }, [onHoverEnd]);

    /**
     * Effect hook for adBreak activity changing.
     */
    useEffect(() => {
        if (isSeekingDisabled) {
            if (isHovering) onMouseLeave();
            if (isGestureInProgress) onGestureEnded();
        }
    }, [isSeekingDisabled]);

    useEventListener('mousemove', onMouseMove, window);
    useEventListener('mouseup', onMouseUp, window);
    useEventListener('click', onClick, window);

    return (
        <SeekingContainer
            data-test="SeekingManager-Container"
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={onClick}
            {...{ isGestureInProgress }}
        />
    );
};
